import { cn } from "@/lib/cn";

export interface GlassCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Adds hover lift + flamingo glow. Use for tappable cards. */
  interactive?: boolean;
  /** Padding scale. */
  padding?: "none" | "sm" | "md" | "lg";
  as?: React.ElementType;
}

const paddingMap = {
  none: "",
  sm: "p-4",
  md: "p-5 sm:p-6",
  lg: "p-6 sm:p-8",
} as const;

/**
 * Frosted glass surface: translucent white fill, hairline border, backdrop
 * blur, and a subtle top inner-highlight for depth. The base of most cards.
 */
export function GlassCard({
  interactive = false,
  padding = "md",
  as: Tag = "div",
  className,
  children,
  ...props
}: GlassCardProps) {
  return (
    <Tag
      className={cn(
        "relative rounded-2xl border border-white/8 bg-white/[0.025]",
        "shadow-glass backdrop-blur-xl",
        // top inner highlight
        "before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-px before:rounded-t-2xl before:bg-linear-to-r before:from-transparent before:via-white/15 before:to-transparent",
        interactive &&
          "transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-0.5 hover:border-flamingo/25 hover:shadow-glow-sm active:translate-y-0",
        paddingMap[padding],
        className,
      )}
      {...props}
    >
      {children}
    </Tag>
  );
}
